import { Fragment, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import {
  Button,
  ButtonBar,
  ContentLoader,
  Modal,
  Text,
  TextField,
  useFormField,
  useModalControl,
} from '@frontend/design-system';
import { useAverageProductionAmount, useHasAdminAccess } from '../hooks';
import { StatItem, StatItemData } from './stat-item';
import { StatsMetric } from './stats-metric';

interface RoiProductionValueStatsMetricProps {
  appointmentsCount?: number;
  isLoading?: boolean;
  title?: string;
}

const widthWithEdit = 130;
const widthWithoutEdit = 100;

export const RoiProductionValueStatsMetric = ({
  appointmentsCount = 0,
  isLoading,
  title,
}: RoiProductionValueStatsMetricProps) => {
  const { t } = useTranslation('analytics');
  const hasAdminAccess = useHasAdminAccess();

  const {
    averageProductionAmount,
    isLoading: isLoadingAverageProductionAmount,
    updateAverageProductionAmount,
  } = useAverageProductionAmount();

  const modalControl = useModalControl();
  const filedProps = useFormField(
    {
      type: 'text',
      value: `$${averageProductionAmount?.value?.toString() || '0'}`,
      required: true,
      transformValue: (value) => {
        if (!value) {
          return '$';
        }

        const numericValue = value.replace(/[^0-9]/g, '');
        return numericValue ? `$${numericValue}` : '$';
      },
    },
    [modalControl.modalProps.show]
  );

  const updatedStats: StatItemData[] = useMemo(() => {
    const avgStatWidth = hasAdminAccess ? widthWithEdit : widthWithoutEdit;
    const restStatWidth = `calc((100% - ${avgStatWidth}px) / 2 - ${theme.spacing(4)})`;

    return [
      {
        label: t('Appointments Completed'),
        value: formatters.value(appointmentsCount),
        infoHoverText: t('Total completed appointments'),
        style: {
          width: restStatWidth,
        },
      },
      {
        label: t('Average Appt Value'),
        value: formatters.shortenCurrency(averageProductionAmount.value),
        infoHoverText: t('Average appointment value'),
        style: {
          width: avgStatWidth,
        },
        ...(hasAdminAccess
          ? {
              onEditClick: modalControl.openModal,
              editIconTooltipText: t("Adjust the average appointment value to better reflect your practice's revenue."),
            }
          : {}),
      },

      {
        label: t('Production Value'),
        value: formatters.shortenCurrency(appointmentsCount * averageProductionAmount.value),
        infoHoverText: t(
          'Production Value is calculated by multiplying the total number of completed appointments by the average appointment value'
        ),
        style: {
          width: restStatWidth,
        },
      },
    ];
  }, [appointmentsCount, averageProductionAmount, hasAdminAccess, t, modalControl.openModal]);

  const handleSave = useCallback(async () => {
    const numericValue = filedProps.value.replace(/[^0-9]/g, '');

    if (numericValue) {
      try {
        await updateAverageProductionAmount(parseInt(numericValue));
        modalControl.closeModal();
      } catch (error) {
        console.error('Error updating average production amount:', error);
      }
    }
  }, [updateAverageProductionAmount, filedProps.value, modalControl]);

  return (
    <>
      <StatsMetric
        dataPointsStyle={{
          alignItems: 'center',
        }}
        isLoading={isLoading || isLoadingAverageProductionAmount}
        title={title || t('Production Value')}
        uniqueId='roi-production-value-stats-metric'
      >
        {updatedStats?.map(({ label, isLoading: isLoadingItem, ...rest }, index) => (
          <Fragment key={label}>
            <StatItem isLoading={isLoadingItem || isLoading} label={label} {...rest} />
            {index === 0 && <Icon name='x' color='subdued' size={16} />}
            {index === 1 && <Icon name='equal' color='subdued' size={16} />}
          </Fragment>
        ))}
      </StatsMetric>

      {hasAdminAccess && (
        <Modal {...modalControl.modalProps} disableCloseOnEscape disableCloseOnOverlayClick maxWidth={420}>
          <Modal.Header onClose={isLoadingAverageProductionAmount ? undefined : modalControl.closeModal}>
            {t('Update Average Appointment Value')}
          </Modal.Header>
          <Modal.Body>
            <TextField
              {...filedProps}
              data-testid='textfield'
              label={t('Average Appointment Value')}
              name='averageAppointmentValue'
            />
            <div css={styles.infoWrapper}>
              <Icon color='primary' name='info-filled' />
              <Text>{t('Updates here will be reflected in the overall production value for all locations')}</Text>
            </div>
            <ContentLoader show={isLoadingAverageProductionAmount} size='small' />
          </Modal.Body>
          <Modal.Footer>
            <ButtonBar>
              <Button disabled={isLoadingAverageProductionAmount} onClick={modalControl.closeModal} variant='secondary'>
                {t('Cancel')}
              </Button>
              <Button
                disabled={filedProps.value === '' || filedProps.value === '$' || isLoadingAverageProductionAmount}
                onClick={handleSave}
              >
                {t('Save')}
              </Button>
            </ButtonBar>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

const styles = {
  operator: css`
    color: ${theme.font.colors.subdued};
    font-weight: ${theme.font.weight.bold};
  `,

  infoWrapper: css`
    align-items: center;
    background-color: ${theme.colors.primary5};
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.primary50};
    display: flex;
    font-size: ${theme.font.size.medium};
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2)};

    > svg {
      height: ${theme.spacing(3)};
      min-height: ${theme.spacing(3)};
      min-width: ${theme.spacing(3)};
      width: ${theme.spacing(3)};
    }
  `,
};
